import React, {useCallback, memo, useState, useEffect} from "react";
import {
    Form,
    Input,
    Select,
    DatePicker,
    Space,
    Avatar,
    Button,
    message,
    ConfigProvider, Checkbox,
} from "antd";
import {FiPaperclip} from "react-icons/fi";
import {DeleteOutlined} from "@ant-design/icons";
import {priorities} from "@/components/taskManagement/PriorityDropdown";
import dayjs from "dayjs";

const {TextArea} = Input;
const {Option} = Select;

// Memoized form components to prevent unnecessary re-renders
const MemoizedInput = memo(({value, onChange, placeholder}) => (
    <Input size="large" value={value} onChange={onChange} placeholder={placeholder}/>
));

const MemoizedTextArea = memo(({value, onChange, placeholder}) => (
    <TextArea
        size="large"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
    />
));

const MemoizedSelect = memo(
    ({value, onChange, placeholder, options, optionRenderer}) => (
        <Select
            className="text-[14px]"
            size="large"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
        >
            {options.map(optionRenderer)}
        </Select>
    )
);

const FileItem = memo(({file, onRemove}) => (
    <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-1">
        <div className="flex items-center space-x-2">
            <FiPaperclip className="w-4 h-4"/>
            <span className="text-sm truncate max-w-[200px]">{file.name}</span>
        </div>
        <Button
            type="text"
            danger
            icon={<DeleteOutlined/>}
            onClick={onRemove}
            size="small"
        />
    </div>
));

const TaskForm = ({
                      initialData,
                      onSubmit,
                      onCancel,
                      type = 'add',
                      statuses,
                      labels,
                      lists,
                      users,
                      boardId,
                  }) => {
    // Internal form state
    const [form, setForm] = useState({
        title: '',
        description: '',
        status_id: null,
        due_date: null,
        priority: null,
        assigned_to: null,
        label_ids: [],
        documents: null,
        deleted_attachments: [],
        budget: '',
        listId: lists?.[0]?.id,
        is_private: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize form with data if editing
    useEffect(() => {
        if (initialData && type === 'edit') {
            setForm({
                title: initialData.title || '',
                description: initialData.description || '',
                status_id: initialData.status_id || null,
                due_date: initialData.due_date || null,
                priority: initialData.priority || null,
                assigned_to: initialData.assigned_to || null,
                label_ids: initialData.labels?.map(l => l.id) || [],
                documents: null,
                deleted_attachments: [],
                budget: initialData.budget || '',
                listId: initialData.list_id || lists?.[0]?.id,
                is_private: initialData.is_private || false
            });
        }
    }, [initialData, type]);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;

        // Validation
        if (!form.title) {
            message.error('Please enter title');
            return;
        }
        if (!form.status_id) {
            message.error('Please enter status');
            return;
        }
        if (typeof form.priority === 'undefined' || form.priority === null) {
            message.error('Please select priority');
            return;
        }

        try {
            setIsSubmitting(true);
            // Create FormData
            const formData = new FormData();

            // Append common fields
            const commonFields = {
                title: form.title,
                description: form.description || '',
                status_id: form.status_id,
                priority: form.priority,
                budget: form.budget || '',
                assigned_to: form.assigned_to,
                due_date: form.due_date,
            };

            Object.entries(commonFields).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });

            // Handle arrays
            if (form.label_ids?.length) {
                form.label_ids.forEach(labelId => {
                    formData.append('label_ids[]', labelId);
                });
            }

            if (form.documents?.length) {
                form.documents.forEach(file => {
                    formData.append('documents[]', file);
                });
            }

            // Add list_id and board_id for new tasks
            if (type === 'add') {
                formData.append('list_id', form.listId || lists?.[0]?.id);
                formData.append('board_id', boardId);
            } else if (form.deleted_attachments?.length) {
                form.deleted_attachments.forEach(id => {
                    formData.append('deleted_attachments[]', id);
                });
            }

            formData.append('is_private', form.is_private ? 1 : 0);

            await onSubmit(formData, type);
        } catch (error) {
            console.error('Error saving task:', error);
            message.error(error.response?.data?.message || 'An error occurred while saving the task');
        } finally {
            setIsSubmitting(false);
        }
    }, [form, type, onSubmit, boardId, isSubmitting]);

    const handleInputChange = useCallback((field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleFileUpload = useCallback((files) => {
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        const maxTotalSize = 50 * 1024 * 1024; // 50MB

        if (totalSize > maxTotalSize) {
            message.error("Total file size cannot exceed 50MB");
            return;
        }

        setForm(prev => ({
            ...prev,
            documents: files
        }));
    }, []);

    const handleFileChange = useCallback((event) => {
        handleFileUpload(Array.from(event.target.files));
    }, [handleFileUpload]);

    const handleDeleteAttachment = useCallback((attachmentId) => {
        setForm(prev => ({
            ...prev,
            deleted_attachments: [...(prev.deleted_attachments || []), attachmentId],
        }));
    }, []);

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorTextPlaceholder: '#666666',
                    lineHeight: 1.5,
                    lineHeightLG: 1.5,
                },
            }}
        >
            <Form
                requiredMark={false}
                size="large"
                layout="vertical"
                className="gaa-task-form sm:grid sm:grid-cols-2 gap-y-0 gap-x-4"
            >
                <Form.Item
                    rules={[{required: true, message: "Please input title"}]}
                    className={"col-span-2"}
                >
                    <MemoizedInput
                        value={form.title}
                        onChange={(e) => handleInputChange("title", e.target.value)}
                        placeholder="Task title"
                    />
                </Form.Item>

                <Form.Item className="mb-4 col-span-2">
                    <MemoizedTextArea
                        value={form.description}
                        onChange={(e) => handleInputChange("description", e.target.value)}
                        placeholder="Task description"
                    />
                </Form.Item>

                <Form.Item
                    rules={[{required: true, message: "Please input status!"}]}
                >
                    <MemoizedSelect
                        value={form.status_id}
                        onChange={(value) => handleInputChange("status_id", value)}
                        placeholder="Select status"
                        options={statuses}
                        optionRenderer={(status) => (
                            <Option
                                className="mb-1 text-center text-white"
                                style={{backgroundColor: status.color}}
                                key={status.id}
                                value={status.id}
                            >
                                {status.name}
                            </Option>
                        )}
                    />
                </Form.Item>

                <Form.Item>
                    <MemoizedSelect
                        value={form.priority}
                        onChange={(value) => handleInputChange("priority", value)}
                        placeholder="Select priority"
                        options={priorities}
                        optionRenderer={(priority) => (
                            <Option
                                className="mb-1 text-center text-white"
                                style={{backgroundColor: priority.bgColor}}
                                key={priority.id}
                                value={priority.id}
                            >
                                {priority.name}
                            </Option>
                        )}
                    />
                </Form.Item>

                <Form.Item>
                    <Select
                        value={form.assigned_to}
                        onChange={(value) => handleInputChange("assigned_to", value)}
                        placeholder="Select assignee"
                        allowClear
                        optionLabelProp="label"
                    >
                        {users.map((user) => (
                            <Option
                                key={user.id}
                                value={user.id}
                                label={
                                    <div className="flex items-center gap-2 ml-2">
                                        <Avatar
                                            size="small"
                                            src={`/${user.profile_image}`}
                                        >
                                            {!user.profile_image && user.name.charAt(0)}
                                        </Avatar>
                                        <span className="truncate">
                                                {user.name}
                                            </span>
                                    </div>
                                }
                            >
                                <div className="flex items-center gap-2 py-1 ml-2">
                                    <Avatar
                                        src={`/${user.profile_image}`}
                                        size="small"
                                    >
                                        {!user.profile_image && user.name.charAt(0)}
                                    </Avatar>
                                    <span className="truncate">
                                            {user.name}
                                        </span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item>
                    <DatePicker
                        className="w-full py-2"
                        value={form.due_date ? dayjs(form.due_date) : null}
                        onChange={(date) => handleInputChange("due_date", date)}
                        placeholder="Select due date"
                    />
                </Form.Item>

                <Form.Item>
                    <div className="themeNewInputGroup inputFile">
                        <label style={{padding: "2px 6px 2px 6px"}}>
                                <span>
                                    <FiPaperclip className="w-4 h-4 inline-block mr-2"/>
                                    {form.documents?.length > 0
                                        ? `${form.documents.length} file(s) selected`
                                        : "No files chosen..."}
                                </span>
                            <label
                                htmlFor="document"
                                style={{width: 100}}
                                className="form-control-placeholder px-2 py-1.5"
                            >
                                Upload Files
                                <input
                                    type="file"
                                    id="document"
                                    onChange={handleFileChange}
                                    multiple
                                    name="documents[]"
                                />
                            </label>
                        </label>
                    </div>

                    {form.documents?.map((file, index) => (
                        <FileItem
                            key={index}
                            file={file}
                            onRemove={() => {
                                const newFiles = [...form.documents];
                                newFiles.splice(index, 1);
                                handleFileUpload(newFiles);
                            }}
                        />
                    ))}

                    {initialData?.attachments?.length > 0 && (
                        <div className="mt-4">
                            <h5 className="text-sm font-medium mb-2">
                                Existing Attachments
                            </h5>
                            {initialData.attachments
                                .filter(
                                    (att) =>
                                        !form.deleted_attachments?.includes(att.id)
                                )
                                .map((attachment) => (
                                    <FileItem
                                        key={attachment.id}
                                        file={attachment}
                                        onRemove={() =>
                                            handleDeleteAttachment(attachment.id)
                                        }
                                    />
                                ))}
                        </div>
                    )}
                </Form.Item>

                <Form.Item>
                    <Input
                        className="w-full"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Task budget"
                        value={form.budget}
                        onChange={(e) => handleInputChange("budget", e.target.value)}
                    />
                </Form.Item>

                {lists?.length > 1 && (
                    <>
                        <Form.Item>
                            <MemoizedSelect
                                value={form.listId}
                                onChange={(value) => handleInputChange("listId", value)}
                                placeholder="Select a List"
                                options={lists}
                                optionRenderer={(list) => (
                                    <Option key={list.id} value={list.id}>
                                        {list.name}
                                    </Option>
                                )}
                            />
                        </Form.Item>
                    </>
                )}

                <Form.Item className="mb-0 col-span-2">
                    <Checkbox
                        checked={form.is_private}
                        onChange={(e) => handleInputChange("is_private", e.target.checked)}
                        className={"flex"}
                    >
                        Make this task private (only visible to creator and assignee)
                    </Checkbox>
                </Form.Item>

                <Button
                    className={"text-sm font-semibold leading-tight px-5 py-2.5 mr-6 relative items-center rounded-full justify-center border border-gray-200"}
                    onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    className="text-sm font-semibold leading-tight px-5 py-2.5 relative items-center rounded-full justify-center border border-gray-200 bg-[#3d8bfd]"
                >
                    {type === 'add' ? 'Create Task' : 'Update Task'}
                </Button>
            </Form>
        </ConfigProvider>
    );
};

export default memo(TaskForm);
